export const QUIZ_CSV_COLUMN_COUNT = 7;

/** Required header row for CSV import/export (row 1). */
export const QUIZ_CSV_HEADERS = [
  "question_name",
  "option_one",
  "option_one_points",
  "option_two",
  "option_two_points",
  "option_three",
  "option_three_points",
];

export const QUIZ_FIELD_MAX = 350;
export const QUIZ_OPTION_MAX = 100;
export const QUIZ_POINTS_MIN = 1;
export const QUIZ_POINTS_MAX = 100;
export const QUIZ_DEFAULT_OPTION_POINTS = 1;
export const QUIZ_PAGE_SIZES = [10, 25, 50, 100];

export const QUIZ_OPTION_POINT_KEYS = [
  "option_one_points",
  "option_two_points",
  "option_three_points",
];

export function emptyQuizQuestion(id) {
  return {
    id,
    question_name: "",
    option_one: "",
    option_one_points: QUIZ_DEFAULT_OPTION_POINTS,
    option_two: "",
    option_two_points: QUIZ_DEFAULT_OPTION_POINTS,
    option_three: "",
    option_three_points: QUIZ_DEFAULT_OPTION_POINTS,
    correct_answer: "",
  };
}
export const QUIZ_MAX_QUESTIONS = 10;
export const QUIZ_MIN_QUESTIONS = 2;

export const SAMPLE_CSV_ROWS = [
  [
    "What is the foundation of effective teamwork?",
    "Trust",
    "10",
    "Silence",
    "1",
    "Conflict",
    "1",
  ],
  [
    "Which skill helps teams share ideas clearly?",
    "Listening",
    "10",
    "Hiding",
    "1",
    "Ignoring",
    "1",
  ],
];

export function buildSampleCsvContent() {
  const escape = (val) => {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [
    QUIZ_CSV_HEADERS.join(","),
    ...SAMPLE_CSV_ROWS.map((row) => row.map(escape).join(",")),
  ];
  return lines.join("\r\n");
}

function downloadCsvBlob(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadSampleQuizCsv() {
  downloadCsvBlob(buildSampleCsvContent(), "quiz_questions_sample.csv");
}

export function exportQuizCsv(questions) {
  const escape = (val) => {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = (questions || []).map((q) => {
    const c = clampQuizFields(q);
    return [
      c.question_name,
      c.option_one,
      c.option_one_points,
      c.option_two,
      c.option_two_points,
      c.option_three,
      c.option_three_points,
    ]
      .map(escape)
      .join(",");
  });
  const content = [QUIZ_CSV_HEADERS.join(","), ...rows].join("\r\n");
  downloadCsvBlob(content, "quiz_questions_export.csv");
}

/** Parse a single CSV line respecting quoted fields */
function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function normalizeHeader(h) {
  return String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

const HEADER_ALIASES = {
  question_name: "question_name",
  question: "question_name",
  questionname: "question_name",
  option_one: "option_one",
  option1: "option_one",
  option_1: "option_one",
  option_two: "option_two",
  option2: "option_two",
  option_2: "option_two",
  option_three: "option_three",
  option3: "option_three",
  option_3: "option_three",
  option_one_points: "option_one_points",
  option1_points: "option_one_points",
  option_1_points: "option_one_points",
  option_two_points: "option_two_points",
  option2_points: "option_two_points",
  option_2_points: "option_two_points",
  option_three_points: "option_three_points",
  option3_points: "option_three_points",
  option_3_points: "option_three_points",
};

export const QUIZ_CSV_HEADER_REQUIRED_MESSAGE =
  "CSV cannot be uploaded without a header row. Row 1 must contain: question_name, option_one, option_one_points, option_two, option_two_points, option_three, option_three_points. Download Sample CSV for the correct format.";

function mapHeaderIndex(headers) {
  const index = {};
  headers.forEach((h, i) => {
    const key = HEADER_ALIASES[normalizeHeader(h)];
    if (key && index[key] === undefined) index[key] = i;
  });
  return index;
}

function looksLikeHeaderRow(cells) {
  const mapped = cells.map((c) => HEADER_ALIASES[normalizeHeader(c)] || normalizeHeader(c));
  const known = new Set(QUIZ_CSV_HEADERS);
  const matches = mapped.filter((m) => known.has(m)).length;
  return matches >= 4;
}

function validateHeaderRow(headerCells) {
  if (!looksLikeHeaderRow(headerCells)) {
    throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);
  }

  if (headerCells.length !== QUIZ_CSV_COLUMN_COUNT) {
    throw new Error(
      `Invalid header row: expected ${QUIZ_CSV_COLUMN_COUNT} columns (${QUIZ_CSV_HEADERS.join(", ")}), found ${headerCells.length}.`
    );
  }

  const col = mapHeaderIndex(headerCells);
  const missing = QUIZ_CSV_HEADERS.filter((k) => col[k] === undefined);
  if (missing.length) {
    throw new Error(
      `Invalid header row. Missing columns: ${missing.join(", ")}. Expected: ${QUIZ_CSV_HEADERS.join(", ")}`
    );
  }

  return col;
}

export function parseQuizCsv(text) {
  const raw = String(text ?? "").replace(/^\uFEFF/, "").trim();
  if (!raw) {
    throw new Error("CSV file is empty.");
  }

  const lines = raw.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);
  }

  const headerCells = parseCsvLine(lines[0]);
  const col = validateHeaderRow(headerCells);

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const lineNum = i + 1;
    const cells = parseCsvLine(lines[i]);
    if (cells.every((c) => !c.trim())) continue;

    if (cells.length !== QUIZ_CSV_COLUMN_COUNT) {
      throw new Error(
        `Row ${lineNum}: expected ${QUIZ_CSV_COLUMN_COUNT} columns, found ${cells.length}.`
      );
    }

    const row = {
      question_name: String(cells[col.question_name] ?? "").trim(),
      option_one: String(cells[col.option_one] ?? "").trim(),
      option_one_points:
        col.option_one_points !== undefined
          ? String(cells[col.option_one_points] ?? "").trim()
          : String(QUIZ_DEFAULT_OPTION_POINTS),
      option_two: String(cells[col.option_two] ?? "").trim(),
      option_two_points:
        col.option_two_points !== undefined
          ? String(cells[col.option_two_points] ?? "").trim()
          : String(QUIZ_DEFAULT_OPTION_POINTS),
      option_three: String(cells[col.option_three] ?? "").trim(),
      option_three_points:
        col.option_three_points !== undefined
          ? String(cells[col.option_three_points] ?? "").trim()
          : String(QUIZ_DEFAULT_OPTION_POINTS),
      correct_answer: "",
    };

    if (
      !row.question_name &&
      !row.option_one &&
      !row.option_two &&
      !row.option_three
    ) {
      continue;
    }
    rows.push(row);
  }

  if (!rows.length) {
    throw new Error("No question rows found in CSV (header row only).");
  }

  return rows;
}

export function clampQuizPoints(raw) {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (digits === "") return QUIZ_DEFAULT_OPTION_POINTS;
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n)) return QUIZ_DEFAULT_OPTION_POINTS;
  return Math.min(QUIZ_POINTS_MAX, Math.max(QUIZ_POINTS_MIN, n));
}

const QUIZ_POINT_FIELDS = [
  { key: "option_one_points", label: "Option one points (option_one_points)" },
  { key: "option_two_points", label: "Option two points (option_two_points)" },
  { key: "option_three_points", label: "Option three points (option_three_points)" },
];

function formatQuizRowMessage(rowLabel, text) {
  return rowLabel ? `${rowLabel} — ${text}` : text;
}

export function validateQuizPointsValue(raw, fieldLabel, rowLabel = "") {
  const s = String(raw ?? "").trim();
  if (s === "") {
    return { ok: true, value: QUIZ_DEFAULT_OPTION_POINTS };
  }
  if (!/^\d+$/.test(s)) {
    return {
      ok: false,
      code: "POINTS",
      message: formatQuizRowMessage(
        rowLabel,
        `${fieldLabel} must be a whole number between ${QUIZ_POINTS_MIN} and ${QUIZ_POINTS_MAX}.`
      ),
    };
  }
  const n = parseInt(s, 10);
  if (n < QUIZ_POINTS_MIN || n > QUIZ_POINTS_MAX) {
    return {
      ok: false,
      code: "POINTS",
      message: formatQuizRowMessage(
        rowLabel,
        `${fieldLabel} must be between ${QUIZ_POINTS_MIN} and ${QUIZ_POINTS_MAX} (found ${n}).`
      ),
    };
  }
  return { ok: true, value: n };
}

export function clampQuizFields(row) {
  return {
    question_name: String(row.question_name ?? "").trim().slice(0, QUIZ_FIELD_MAX),
    option_one: String(row.option_one ?? "").trim().slice(0, QUIZ_OPTION_MAX),
    option_one_points: clampQuizPoints(row.option_one_points),
    option_two: String(row.option_two ?? "").trim().slice(0, QUIZ_OPTION_MAX),
    option_two_points: clampQuizPoints(row.option_two_points),
    option_three: String(row.option_three ?? "").trim().slice(0, QUIZ_OPTION_MAX),
    option_three_points: clampQuizPoints(row.option_three_points),
    correct_answer: "",
  };
}

const QUIZ_IMPORT_FIELD_LIMITS = [
  { key: "question_name", label: "Question (question_name)", max: QUIZ_FIELD_MAX },
  { key: "option_one", label: "Option one (option_one)", max: QUIZ_OPTION_MAX },
  { key: "option_two", label: "Option two (option_two)", max: QUIZ_OPTION_MAX },
  { key: "option_three", label: "Option three (option_three)", max: QUIZ_OPTION_MAX },
];

/** Strict import checks — rejects over-length fields before saving. */
export function validateQuizRowForImport(row, rowLabel = "Row") {
  for (const { key, label, max } of QUIZ_IMPORT_FIELD_LIMITS) {
    const value = String(row[key] ?? "").trim();
    if (value.length > max) {
      return {
        ok: false,
        code: "CHAR_LIMIT",
        message: `${rowLabel} — ${label} has ${value.length} characters. Maximum allowed is ${max} characters. Please shorten the text and upload again.`,
      };
    }
  }

  for (const { key, label } of QUIZ_POINT_FIELDS) {
    const pointsCheck = validateQuizPointsValue(row[key], label, rowLabel);
    if (!pointsCheck.ok) return pointsCheck;
  }

  if (!String(row.question_name ?? "").trim()) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Question name is required."),
    };
  }

  if (
    !String(row.option_one ?? "").trim() ||
    !String(row.option_two ?? "").trim() ||
    !String(row.option_three ?? "").trim()
  ) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "All three options are required."),
    };
  }

  const q = clampQuizFields({
    ...row,
    correct_answer: "",
    option_one_points: validateQuizPointsValue(row.option_one_points, "option_one_points").value,
    option_two_points: validateQuizPointsValue(row.option_two_points, "option_two_points").value,
    option_three_points: validateQuizPointsValue(row.option_three_points, "option_three_points").value,
  });

  return { ok: true, data: q };
}

export function validateQuizRow(row, rowLabel = "") {
  if (!String(row.question_name ?? "").trim()) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Question name is required."),
    };
  }

  if (
    !String(row.option_one ?? "").trim() ||
    !String(row.option_two ?? "").trim() ||
    !String(row.option_three ?? "").trim()
  ) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "All three options are required."),
    };
  }

  for (const { key, label } of QUIZ_POINT_FIELDS) {
    const pointsCheck = validateQuizPointsValue(row[key], label, rowLabel);
    if (!pointsCheck.ok) return pointsCheck;
  }

  const q = clampQuizFields({
    ...row,
    option_one_points: validateQuizPointsValue(row.option_one_points, "option_one_points").value,
    option_two_points: validateQuizPointsValue(row.option_two_points, "option_two_points").value,
    option_three_points: validateQuizPointsValue(row.option_three_points, "option_three_points").value,
  });

  return { ok: true, data: q };
}

export function validateQuizList(questions) {
  if (questions.length < QUIZ_MIN_QUESTIONS) {
    return {
      ok: false,
      message: `Add at least ${QUIZ_MIN_QUESTIONS} questions before saving.`,
    };
  }
  if (questions.length > QUIZ_MAX_QUESTIONS) {
    return { ok: false, message: `You can save up to ${QUIZ_MAX_QUESTIONS} questions.` };
  }

  const normalized = [];
  for (let i = 0; i < questions.length; i++) {
    const result = validateQuizRow(questions[i], `Question ${i + 1}`);
    if (!result.ok) {
      return { ok: false, message: result.message };
    }
    normalized.push(result.data);
  }

  return { ok: true, data: normalized };
}
